import { type FlowRecords, formatDuration, type LevelRecord, type Solve } from '@arrow/game-engine';
import { type Level, LEVELS } from '@arrow/puzzle-schema';
import type { ReactNode } from 'react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger, Tabs, TabsContent, TabsList, TabsTrigger, VisuallyHidden } from '../../ui/index.ts';
import { LEVEL_NAME } from '../today-model.ts';
import type { TakenTally } from '../../platform/challenges-taken.ts';
import { type ChallengeTally, helpWords, longDate, plural, shortDate, takenLine } from './model.ts';

/** A section of the page: a rule, an italic heading, and an optional aside on its right. */
export function Section({ id, title, aside, children, className }: { id: string; title: string; aside?: ReactNode; children: ReactNode; className?: string }) {
	return (
		<section className={className ? `st-sec ${className}` : 'st-sec'} aria-labelledby={id}>
			<div className="st-sec-head">
				<h2 className="st-h2" id={id}>
					{title}
				</h2>
				{aside}
			</div>
			{children}
		</section>
	);
}

/** One figure: a numeral over its label. */
export function Figure({ value, label }: { value: ReactNode; label: string }) {
	return (
		<div className="st-fig">
			<span className="st-fig-n">{value}</span>
			<span className="st-fig-lb">{label}</span>
		</div>
	);
}

/**
 * The three levels as record cards that also pick the level below: your best time on each.
 * They're tabs (ui/Tabs): the arrows move between them, and `children` is the chosen
 * level's panel, named by its card.
 */
export function LevelCards({
	records,
	level,
	onPick,
	children,
}: {
	records: Record<Level, LevelRecord>;
	level: Level;
	onPick: (l: Level) => void;
	children: ReactNode;
}) {
	return (
		<Tabs value={level} onValueChange={(v) => onPick(v as Level)}>
			<TabsList className="st-cards" aria-label="Pick a level">
				{LEVELS.map((l) => {
					const r = records[l];
					return (
						<TabsTrigger key={l} value={l} className="st-card">
							<span className="st-card-lv">{LEVEL_NAME[l]}</span>
							<span className="st-card-n">
								{r.bestMs !== null ? formatDuration(r.bestMs) : '–'}
								<VisuallyHidden>{r.bestMs !== null ? ' best time' : ' no best time yet'}</VisuallyHidden>
							</span>
							<span className="st-card-sub">{r.solved ? `${r.solved} solved` : 'Not yet'}</span>
						</TabsTrigger>
					);
				})}
			</TabsList>
			<TabsContent value={level} className="st-level">
				{children}
			</TabsContent>
		</Tabs>
	);
}

/** Every solve at a level, newest first, as a table (folded away). */
export function EveryTime({ level, solves, thisYear, onOpen }: { level: Level; solves: Solve[]; thisYear: number; onOpen: () => void }) {
	return (
		<Collapsible
			className="st-every"
			onOpenChange={(open) => {
				if (open) onOpen();
			}}
		>
			<CollapsibleTrigger className="st-every-t">
				<span>
					Every {LEVEL_NAME[level]} time <span className="st-every-n">({solves.length})</span>
				</span>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<table className="st-table">
					<caption className="sr-only">Every {LEVEL_NAME[level]} solve, newest first</caption>
					<thead>
						<tr>
							<th scope="col">Day</th>
							<th scope="col">Time</th>
							<th scope="col">Help</th>
							<th scope="col">Flow</th>
						</tr>
					</thead>
					<tbody>
						{[...solves].reverse().map((s) => (
							<tr key={s.dateKey}>
								<th scope="row">
									{Number(s.dateKey.slice(0, 4)) === thisYear ? shortDate(s.dateKey) : longDate(s.dateKey)}
									{!s.onTime && <span className="st-late"> · later</span>}
								</th>
								<td>{formatDuration(s.elapsedMs)}</td>
								<td className={s.checksUsed || s.revealsUsed ? undefined : 'st-perfect'}>{helpWords(s)}</td>
								<td>{s.bestFlow >= 2 ? `×${s.bestFlow}` : '–'}</td>
							</tr>
						))}
					</tbody>
				</table>
			</CollapsibleContent>
		</Collapsible>
	);
}

/** Flow: your longest chain (in Flow's cobalt) and your typical one. */
export function FlowBody({ flow, thisYear }: { flow: FlowRecords; thisYear: number }) {
	const total = flow.none + flow.spread.reduce((n, [, count]) => n + count, 0);
	return (
		<div className="st-flow-top">
			<span className="st-flow-tag" aria-hidden="true">
				Flow ×{flow.best}
			</span>
			<p className="st-flow-what">
				<span className="sr-only">Best Flow {flow.best}. </span>
				Your longest chain{flow.bestLevel && flow.bestOn ? `, on ${LEVEL_NAME[flow.bestLevel]}, ${longDate(flow.bestOn, thisYear)}` : ''}.
				{total >= 3 && flow.typical !== null && flow.typical >= 2 ? <> Typically ×{flow.typical}.</> : null}
			</p>
		</div>
	);
}

/** Your challenges: how many you sent, how many friends played, and how many beat you. */
export function ChallengesBody({
	sent,
	tally,
	loading,
	asked,
	taken,
}: {
	sent: number;
	tally: ChallengeTally | null;
	loading: boolean;
	asked: number;
	/** The challenges you took, from this device's records. */
	taken: TakenTally;
}) {
	const dash = loading ? '…' : '–';
	return (
		<>
			{sent > 0 && (
				<>
					<div className="st-figs st-figs-3">
						<Figure value={sent} label="Sent" />
						<Figure value={tally ? tally.played : dash} label="Played" />
						<Figure value={tally ? tally.beat : dash} label="Beat you" />
					</div>
					<p className="st-note" aria-live="polite">
						{tally && tally.counted > 0
							? tally.finished > 0
								? `You held off ${tally.heldOff} of the ${tally.finished} ${plural(tally.finished, 'friend')} who finished${asked < sent ? `, over your last ${asked}` : ''}.`
								: 'Nobody has finished one yet.'
							: loading
								? 'Asking how your friends got on…'
								: 'Results show up here once friends play.'}
					</p>
				</>
			)}
			{/* The ones you took: the line says both numbers ("You've beaten 3 of 5 challengers"). */}
			{taken.taken > 0 && <p className="st-note">{takenLine(taken)}</p>}
		</>
	);
}
